'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn';
import type { DateRange } from './document-list-shell-types';

interface PeriodProps {
  period: DateRange;
  onChange: (p: DateRange) => void;
  className?: string;
}

/**
 * Date range picker cu preset-uri rapide (Azi, Săptămâna, Luna curentă, Q1,
 * Anul curent, etc.) + input custom de la / până la. Generalizare a
 * VanzariDatePicker-ului din /facturi, fără dependență de --v-* tokens.
 */
export function DocumentListShellPeriod({ period, onChange, className }: PeriodProps) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(period.from);
  const [customTo, setCustomTo] = useState(period.to);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (ref.current && ref.current.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => { setCustomFrom(period.from); setCustomTo(period.to); }, [period.from, period.to, open]);

  const fmt = (d: string) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' });
  };
  const label = period.from && period.to ? `${fmt(period.from)} – ${fmt(period.to)}` : 'Alege perioada';

  const applyPreset = (type: string) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const toStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let from = '', to = '';
    if (type === 'today') { from = to = toStr(now); }
    else if (type === 'week') { const s = new Date(now); s.setDate(now.getDate() - 6); from = toStr(s); to = toStr(now); }
    else if (type === 'thisMonth') { from = toStr(new Date(y, m, 1)); to = toStr(new Date(y, m + 1, 0)); }
    else if (type === 'lastMonth') { from = toStr(new Date(y, m - 1, 1)); to = toStr(new Date(y, m, 0)); }
    else if (type === 'last3') { from = toStr(new Date(y, m - 2, 1)); to = toStr(new Date(y, m + 1, 0)); }
    else if (type === 'q1') { from = `${y}-01-01`; to = `${y}-03-31`; }
    else if (type === 'thisYear') { from = `${y}-01-01`; to = toStr(now); }
    else if (type === 'lastYear') { from = `${y - 1}-01-01`; to = `${y - 1}-12-31`; }
    onChange({ from, to });
    setOpen(false);
  };

  const applyCustom = () => {
    if (customFrom && customTo) {
      onChange({ from: customFrom, to: customTo });
      setOpen(false);
    }
  };

  const presets = [
    { k: 'today', l: 'Azi' },
    { k: 'week', l: 'Ultimele 7 zile' },
    { k: 'thisMonth', l: 'Luna curentă' },
    { k: 'lastMonth', l: 'Luna trecută' },
    { k: 'last3', l: 'Ultimele 3 luni' },
    { k: 'q1', l: 'Q1 an curent' },
    { k: 'thisYear', l: 'Anul curent' },
    { k: 'lastYear', l: 'Anul trecut' },
  ];

  return (
    <div ref={ref} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={period.from && period.to ? `Perioada ${label}. Apasă pentru a schimba.` : 'Alege perioada'}
      >
        <CalendarIcon /> {label}
        <ChevronIcon />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[320px] rounded-xl border border-neutral-200 bg-white shadow-2xl"
          role="dialog"
          aria-label="Alege perioada"
        >
          <div className="border-b border-neutral-100 p-2">
            <div className="px-2 py-1 text-[0.6563rem] font-bold uppercase tracking-wider text-neutral-500">Perioade rapide</div>
            <div className="grid grid-cols-2 gap-0.5">
              {presets.map((pr) => (
                <button
                  key={pr.k}
                  type="button"
                  onClick={() => applyPreset(pr.k)}
                  className="rounded px-2.5 py-1.5 text-left text-xs font-medium text-neutral-700 hover:bg-neutral-100"
                >{pr.l}</button>
              ))}
            </div>
          </div>
          <div className="p-3">
            <div className="mb-2 text-[0.6563rem] font-bold uppercase tracking-wider text-neutral-500">Perioadă personalizată</div>
            <div className="mb-2 grid grid-cols-2 gap-2">
              <label>
                <span className="block text-[0.625rem] font-semibold text-neutral-600">De la</span>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-0.5 w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-900 outline-none focus:border-primary-500"
                />
              </label>
              <label>
                <span className="block text-[0.625rem] font-semibold text-neutral-600">Până la</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-0.5 w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-900 outline-none focus:border-primary-500"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={applyCustom}
              disabled={!customFrom || !customTo}
              className={cn(
                'w-full rounded px-2 py-1.5 text-xs font-semibold transition-colors',
                customFrom && customTo
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'cursor-not-allowed bg-neutral-100 text-neutral-400',
              )}
            >Aplică perioada</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-60" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
